import { useState, type ChangeEvent } from 'react';
import { ApiError, anonymizeImage, type ImageAnonymizeResponse } from '../lib/api.js';

/**
 * Photo → anonymized illustration card.
 *
 * Direct request from Yuri-san (May 7): senpai and candidates want to
 * share trip imagery without surfacing real people. The model describes
 * the scene (composition, activity, mood) and renders an illustration
 * with no recognizable faces.
 *
 * For v0.1 the result is display-only — we don't persist the
 * illustration into the SenpaiLog corpus yet. The point of the card is
 * to demonstrate the conversion in front of the demo audience and let
 * the user save the output locally if they want it.
 *
 * Storage decision: keeping this off DynamoDB for now. ~1 MB base64
 * PNGs would blow the 400 KB item ceiling. If we want to attach
 * illustrations to a SenpaiLog later, that's an S3 bucket + a URL
 * field — out of scope for this PR.
 */

const MAX_BYTES = 4 * 1024 * 1024; // matches backend cap with headroom

interface State {
  status: 'idle' | 'preview' | 'working' | 'done' | 'error';
  fileName?: string;
  origDataUrl?: string;
  origMime?: string;
  hint?: string;
  result?: ImageAnonymizeResponse;
  error?: string;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function dataUrlToBase64(dataUrl: string): string {
  const idx = dataUrl.indexOf(',');
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function PhotoAnonymizer() {
  const [state, setState] = useState<State>({ status: 'idle' });
  const [hint, setHint] = useState('');

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setState({
        status: 'error',
        error: `画像が大きすぎます (${Math.round(file.size / 1024 / 1024)} MB)。4 MB 以内に圧縮してください。`,
      });
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      setState({
        status: 'error',
        error: `対応していない形式です: ${file.type || '不明'} (jpeg/png/webp のみ)`,
      });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setState({
        status: 'preview',
        fileName: file.name,
        origDataUrl: dataUrl,
        origMime: file.type,
      });
    } catch (err) {
      setState({
        status: 'error',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onAnonymize = async () => {
    if (state.status !== 'preview' || !state.origDataUrl || !state.origMime) return;
    setState({ ...state, status: 'working' });
    try {
      const result = await anonymizeImage({
        image_base64: dataUrlToBase64(state.origDataUrl),
        mime_type: state.origMime,
        ...(hint.trim() ? { hint: hint.trim() } : {}),
      });
      setState({ ...state, status: 'done', result, hint: hint.trim() });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
      setState({ ...state, status: 'error', error: message });
    }
  };

  const onReset = () => {
    setState({ status: 'idle' });
    setHint('');
  };

  const onDownload = () => {
    if (state.status !== 'done' || !state.result) return;
    const dataUrl = `data:${state.result.illustration_mime};base64,${state.result.illustration_b64}`;
    const base = state.fileName?.replace(/\.[^.]+$/, '') ?? 'illustration';
    downloadDataUrl(dataUrl, `${base}-anonymized.png`);
  };

  return (
    <section className="anonymizer-card">
      <h2>写真を匿名イラスト化</h2>
      <p className="muted">
        集合写真や活動の様子を、特定可能な顔を含まないイラストに変換します。
        変換結果はこの画面で確認できます。共有・保存はあなたが選んだ場合のみ行われます。
      </p>

      {state.status === 'error' && state.error && (
        <div className="error">{state.error}</div>
      )}

      {state.status === 'idle' && (
        <div className="anonymizer-dropzone">
          <label className="btn anonymizer-pick-btn">
            写真を選ぶ
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPick}
              hidden
            />
          </label>
          <p className="muted anonymizer-hint">JPEG / PNG / WebP、最大 4 MB</p>
        </div>
      )}

      {(state.status === 'preview' || state.status === 'working') && state.origDataUrl && (
        <div className="anonymizer-stage">
          <figure className="anonymizer-figure">
            <img src={state.origDataUrl} alt="プレビュー" className="anonymizer-image" />
            <figcaption className="muted">{state.fileName}</figcaption>
          </figure>
          <div className="anonymizer-controls">
            <label className="field-label" htmlFor="anonymizer-hint">
              ヒント(任意)
            </label>
            <input
              id="anonymizer-hint"
              className="input"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="例: スタンフォードキャンパス前の集合写真"
              disabled={state.status === 'working'}
            />
            <div className="row row-mt-sm">
              <button
                type="button"
                className="btn-ghost"
                onClick={onReset}
                disabled={state.status === 'working'}
              >
                やり直す
              </button>
              <div className="spacer" />
              <button
                type="button"
                className="btn"
                onClick={onAnonymize}
                disabled={state.status === 'working'}
              >
                {state.status === 'working' ? 'イラスト化しています…' : 'イラストにする'}
              </button>
            </div>
            {state.status === 'working' && (
              <p className="muted">10〜20 秒ほどかかります。シーンを読み取って、新しい絵を描いています。</p>
            )}
          </div>
        </div>
      )}

      {state.status === 'done' && state.result && state.origDataUrl && (
        <div className="anonymizer-result">
          <div className="anonymizer-pair">
            <figure className="anonymizer-figure">
              <figcaption className="muted">元の写真</figcaption>
              <img src={state.origDataUrl} alt="元の写真" className="anonymizer-image" />
            </figure>
            <figure className="anonymizer-figure">
              <figcaption className="muted">匿名イラスト</figcaption>
              <img
                src={`data:${state.result.illustration_mime};base64,${state.result.illustration_b64}`}
                alt="匿名イラスト"
                className="anonymizer-image"
              />
            </figure>
          </div>
          <div className="anonymizer-description">
            <strong>シーン記述:</strong> {state.result.description}
          </div>
          <div className="row row-mt-sm">
            <button type="button" className="btn-ghost" onClick={onReset}>
              別の写真でやる
            </button>
            <div className="spacer" />
            <button type="button" className="btn" onClick={onDownload}>
              イラストをダウンロード
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
